import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { StoreIcon, MapPinIcon, StarIcon, BanknoteIcon, TrendingUpIcon, ClockIcon, ActivityIcon, SearchIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useMavisData, formatZAR } from '../hooks/useMavisData';
// Base locations for generating mock data
const SA_LOCATIONS = [{
  name: 'Soweto',
  lat: -26.2485,
  lng: 27.854
}, {
  name: 'Alexandra',
  lat: -26.1076,
  lng: 28.0997
}, {
  name: 'Sandton',
  lat: -26.1076,
  lng: 28.0567
}, {
  name: 'Randburg',
  lat: -26.0936,
  lng: 28.0067
}, {
  name: 'Roodepoort',
  lat: -26.1625,
  lng: 27.8725
}, {
  name: 'Mamelodi',
  lat: -25.72,
  lng: 28.395
}, {
  name: 'Centurion',
  lat: -25.8603,
  lng: 28.1894
}, {
  name: 'Soshanguve',
  lat: -25.4833,
  lng: 28.0833
}, {
  name: 'Atteridgeville',
  lat: -25.7667,
  lng: 28.0833
}, {
  name: 'Tembisa',
  lat: -25.996,
  lng: 28.2268
}, {
  name: 'Benoni',
  lat: -26.1883,
  lng: 28.3206
}, {
  name: 'Boksburg',
  lat: -26.2125,
  lng: 28.2625
}, {
  name: 'Germiston',
  lat: -26.2167,
  lng: 28.1667
}, {
  name: 'Khayelitsha',
  lat: -34.0388,
  lng: 18.6747
}, {
  name: 'Mitchells Plain',
  lat: -34.0489,
  lng: 18.6181
}, {
  name: 'Gugulethu',
  lat: -33.9781,
  lng: 18.5656
}, {
  name: 'Langa',
  lat: -33.9447,
  lng: 18.5253
}, {
  name: 'Umlazi',
  lat: -29.97,
  lng: 30.89
}, {
  name: 'KwaMashu',
  lat: -29.745,
  lng: 30.975
}, {
  name: 'Pinetown',
  lat: -29.8167,
  lng: 30.8667
}, {
  name: 'Chatsworth',
  lat: -29.9167,
  lng: 30.8833
}, {
  name: 'Bloemfontein',
  lat: -29.1211,
  lng: 26.214
}, {
  name: 'Polokwane',
  lat: -23.9045,
  lng: 29.4689
}, {
  name: 'Nelspruit',
  lat: -25.4753,
  lng: 30.9694
}, {
  name: 'Rustenburg',
  lat: -25.6667,
  lng: 27.2417
}, {
  name: 'Kimberley',
  lat: -28.7282,
  lng: 24.7499
}, {
  name: 'Port Elizabeth',
  lat: -33.9608,
  lng: 25.6022
}, {
  name: 'East London',
  lat: -33.0153,
  lng: 27.9116
}, {
  name: 'Pietermaritzburg',
  lat: -29.6006,
  lng: 30.3794
}];
const VENDOR_PREFIXES = ['Sipho', 'Mama Joy', 'Kasi', 'Zanele', 'Thabo', 'Dube', 'Mamelodi', 'Sunshine', 'Star', 'Freedom', 'Unity', 'Hope', 'Bheki', 'Lindiwe', 'Sizwe'];
const VENDOR_SUFFIXES = ['Spaza', 'Mini-mart', 'Corner Store', 'Trading Post', 'Quick Shop', 'Superette', 'Tuck Shop', 'Cash & Carry', 'Traders', 'Express'];

// Generate 100 mock vendors
const generatedVendors = Array.from({
  length: 100
}, (_, i) => {
  const loc = SA_LOCATIONS[Math.floor(Math.random() * SA_LOCATIONS.length)];
  const prefix = VENDOR_PREFIXES[Math.floor(Math.random() * VENDOR_PREFIXES.length)];
  const suffix = VENDOR_SUFFIXES[Math.floor(Math.random() * VENDOR_SUFFIXES.length)];
  const sales = Math.floor(Math.random() * 25000);
  const rand = Math.random();
  const status = rand < 0.7 ? 'Active' : rand < 0.85 ? 'Warning' : 'Offline';
  return {
    id: `V-${(i + 7).toString().padStart(3, '0')}`,
    name: `${prefix} ${suffix}`,
    location: loc.name,
    lat: loc.lat + (Math.random() - 0.5) * 0.04,
    lng: loc.lng + (Math.random() - 0.5) * 0.04,
    salesToday: sales,
    commission: sales * 0.05,
    status,
    rating: +(Math.random() * 3 + 2).toFixed(1)
  };
});
const getCoordinatesForOriginal = (location: string): [number, number] => {
  if (location.includes('Soweto')) return [-26.2485, 27.854];
  if (location.includes('Khayelitsha')) return [-34.0388, 18.6747];
  if (location.includes('Tembisa')) return [-25.996, 28.2268];
  if (location.includes('Mamelodi')) return [-25.72, 28.395];
  if (location.includes('Umlazi')) return [-29.97, 30.89];
  if (location.includes('Alexandra')) return [-26.1076, 28.0997];
  return [-28.5, 25.5];
};
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return '#8DB43E';
    // mavis-green
    case 'Warning':
      return '#E07020';
    // mavis-orange
    case 'Offline':
      return '#ef4444';
    // red
    default:
      return '#9ca3af';
    // gray
  }
};
const getStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Warning':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'Offline':
      return 'bg-red-50 text-red-600 border-red-100';
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200';
  }
};
export function VendorMap() {
  const data = useMavisData();
  // Combine original 6 vendors with 100 generated ones
  const allVendors = useMemo(() => {
    const originalWithCoords = data.vendors.map(v => {
      const [lat, lng] = getCoordinatesForOriginal(v.location);
      return {
        ...v,
        lat,
        lng
      };
    });
    return [...originalWithCoords, ...generatedVendors];
  }, [data.vendors]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const itemsPerPage = 10;
  // Filter vendors based on search
  const filteredVendors = useMemo(() => {
    if (!searchQuery.trim()) return allVendors;
    const query = searchQuery.toLowerCase();
    return allVendors.filter(v => v.name.toLowerCase().includes(query) || v.location.toLowerCase().includes(query) || v.id.toLowerCase().includes(query));
  }, [allVendors, searchQuery]);
  // Pagination logic
  const totalPages = Math.ceil(filteredVendors.length / itemsPerPage);
  const paginatedVendors = filteredVendors.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);
  // Calculate stats
  const totalVendors = allVendors.length;
  const activeVendors = allVendors.filter(v => v.status === 'Active').length;
  const totalSalesToday = allVendors.reduce((sum, v) => sum + v.salesToday, 0);
  const avgRating = totalVendors > 0 ? (allVendors.reduce((sum, v) => sum + v.rating, 0) / totalVendors).toFixed(1) : '0.0';
  return <div className="relative h-full w-full bg-gray-50 flex" data-id="element-2834">
      {/* Map Container */}
      <div className="flex-1 relative h-full" data-id="element-2835">
        <MapContainer center={[-28.5, 25.5]} zoom={6} className="h-full w-full z-0" zoomControl={false} data-id="element-2836">

          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-2837" />


          {/* Vendor Markers */}
          {allVendors.map(vendor => {
          const color = getStatusColor(vendor.status);
          const isSelected = selectedVendorId === vendor.id;
          return <CircleMarker key={vendor.id} center={[vendor.lat, vendor.lng]} radius={isSelected ? 16 : 12} pathOptions={{
            color: isSelected ? '#0D4F5C' : '#ffffff',
            weight: isSelected ? 4 : 3,
            fillColor: color,
            fillOpacity: isSelected ? 1 : 0.85
          }} eventHandlers={{
            click: () => setSelectedVendorId(vendor.id)
          }} data-id="element-2838">

                <Tooltip direction="top" offset={[0, -12]} opacity={1} data-id="element-2839">
                  <div className="flex items-start gap-2" data-id="element-2840">
                    <div className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{
                  backgroundColor: color
                }} data-id="element-2841" />

                    <div data-id="element-2842">
                      <div className="font-bold text-gray-900" data-id="element-2843">
                        {vendor.name}
                      </div>
                      <div className="text-gray-600 text-xs" data-id="element-2844">
                        {vendor.location}
                      </div>
                      <div className="text-[10px] font-semibold mt-0.5" style={{
                    color
                  }} data-id="element-2845">

                        {vendor.status}
                      </div>
                    </div>
                  </div>
                </Tooltip>

                <Popup maxWidth={320} className="rounded-none" data-id="element-2846">
                  <div className="p-1 min-w-[280px]" data-id="element-2847">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-100" data-id="element-2848">
                      <div data-id="element-2849">
                        <h3 className="font-bold text-lg text-gray-900 m-0 leading-tight" data-id="element-2850">
                          {vendor.name}
                        </h3>
                        <div className="text-xs text-gray-500 font-mono mt-1" data-id="element-2851">
                          {vendor.id}
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-none text-xs font-medium border ${getStatusBadgeClass(vendor.status)}`} data-id="element-2852">

                        <span className="w-2 h-2 rounded-full shrink-0" style={{
                      backgroundColor: color
                    }} data-id="element-2853" />

                        {vendor.status}
                      </span>
                    </div>

                    {/* Location & Rating */}
                    <div className="space-y-2 mb-4" data-id="element-2854">
                      <div className="flex items-center gap-2 text-sm" data-id="element-2855">
                        <MapPinIcon className="w-4 h-4 text-gray-400 shrink-0" data-id="element-2856" />
                        <span className="text-gray-600" data-id="element-2857">{vendor.location}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm" data-id="element-2858">
                        <div className="flex items-center" data-id="element-2859">
                          {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`w-4 h-4 ${star <= Math.floor(vendor.rating) ? 'text-mavis-gold fill-mavis-gold' : 'text-gray-300'}`} data-id="element-2860" />)}
                        </div>
                        <span className="font-medium text-gray-900" data-id="element-2861">
                          {vendor.rating.toFixed(1)}
                        </span>
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 border border-gray-100 rounded-none" data-id="element-2862">
                      <div data-id="element-2863">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-2864">
                          Sales Today
                        </p>
                        <p className="font-mono font-bold text-gray-900 text-lg leading-none" data-id="element-2865">
                          {formatZAR(vendor.salesToday)}
                        </p>
                      </div>
                      <div data-id="element-2866">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1" data-id="element-2867">
                          Commission
                        </p>
                        <p className="font-mono font-bold text-mavis-teal text-lg leading-none" data-id="element-2868">
                          {formatZAR(vendor.commission)}
                        </p>
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div data-id="element-2869">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2" data-id="element-2870">
                        Recent Activity
                      </p>
                      <div className="space-y-2" data-id="element-2871">
                        {[{
                      amount: 150,
                      time: '10 mins ago'
                    }, {
                      amount: 50,
                      time: '1 hour ago'
                    }, {
                      amount: 200,
                      time: '3 hours ago'
                    }].map((txn, i) => <div key={i} className="flex items-center justify-between text-sm border-b border-gray-50 pb-2 last:border-0 last:pb-0" data-id="element-2872">

                            <div className="flex items-center gap-2" data-id="element-2873">
                              <BanknoteIcon className="w-3.5 h-3.5 text-mavis-green" data-id="element-2874" />
                              <span className="text-gray-600 text-xs" data-id="element-2875">
                                Token Sold
                              </span>
                            </div>
                            <div className="text-right" data-id="element-2876">
                              <span className="font-mono font-medium text-gray-900 block leading-none" data-id="element-2877">
                                {formatZAR(txn.amount)}
                              </span>
                              <span className="text-[9px] text-gray-400 flex items-center justify-end gap-1 mt-0.5" data-id="element-2878">
                                <ClockIcon className="w-2.5 h-2.5" data-id="element-2879" /> {txn.time}
                              </span>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </Popup>
              </CircleMarker>;
        })}
        </MapContainer>

        {/* Floating Overlay Stats */}
        <div className="absolute top-4 left-4 right-80 mr-8 z-[1000] pointer-events-none" data-id="element-2880">
          <div className="max-w-7xl mx-auto" data-id="element-2881">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pointer-events-auto" data-id="element-2882">
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-2883">
                <div className="p-2 bg-mavis-teal/10 text-mavis-teal rounded-none" data-id="element-2884">
                  <StoreIcon className="w-4 h-4" data-id="element-2885" />
                </div>
                <div data-id="element-2886">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-2887">
                    Total Vendors
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-2888">
                    {totalVendors}
                  </p>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-2889">
                <div className="p-2 bg-mavis-green/10 text-mavis-green rounded-none" data-id="element-2890">
                  <ActivityIcon className="w-4 h-4" data-id="element-2891" />
                </div>
                <div data-id="element-2892">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-2893">
                    Active Vendors
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-2894">
                    {activeVendors}
                  </p>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-2895">
                <div className="p-2 bg-mavis-blue/10 text-mavis-blue rounded-none" data-id="element-2896">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-2897" />
                </div>
                <div data-id="element-2898">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-2899">
                    Total Sales Today
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-2900">
                    {formatZAR(totalSalesToday)}
                  </p>
                </div>
              </div>
              <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md flex items-center gap-3" data-id="element-2901">
                <div className="p-2 bg-mavis-gold/10 text-mavis-gold rounded-none" data-id="element-2902">
                  <StarIcon className="w-4 h-4" data-id="element-2903" />
                </div>
                <div data-id="element-2904">
                  <p className="text-[10px] text-gray-500 font-medium uppercase leading-tight" data-id="element-2905">
                    Avg Rating
                  </p>
                  <p className="text-lg font-bold text-gray-900 font-mono leading-tight" data-id="element-2906">
                    {avgRating}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Legend */}
        <div className="absolute bottom-6 left-4 z-[1000]" data-id="element-2907">
          <div className="bg-white/95 backdrop-blur-sm p-3 rounded-none border border-gray-200 shadow-md" data-id="element-2908">
            <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-2 border-b border-gray-100 pb-1" data-id="element-2909">
              Vendor Status
            </h4>
            <div className="space-y-2" data-id="element-2910">
              <div className="flex items-center gap-2" data-id="element-2911">
                <div className="w-3 h-3 rounded-full bg-[#8DB43E] border border-white shadow-sm" data-id="element-2912"></div>
                <span className="text-xs font-medium text-gray-700" data-id="element-2913">
                  Active
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2914">
                <div className="w-3 h-3 rounded-full bg-[#E07020] border border-white shadow-sm" data-id="element-2915"></div>
                <span className="text-xs font-medium text-gray-700" data-id="element-2916">
                  Warning
                </span>
              </div>
              <div className="flex items-center gap-2" data-id="element-2917">
                <div className="w-3 h-3 rounded-full bg-[#ef4444] border border-white shadow-sm" data-id="element-2918"></div>
                <span className="text-xs font-medium text-gray-700" data-id="element-2919">
                  Offline
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Vendor List Panel */}
      <div className="absolute right-4 top-4 bottom-6 z-[1000] w-80 bg-white/95 backdrop-blur-sm border border-gray-200 shadow-lg rounded-none flex flex-col" data-id="element-2920">
        {/* Panel Header */}
        <div className="p-4 border-b border-gray-200 bg-gray-50/80" data-id="element-2921">
          <div className="flex justify-between items-center mb-3" data-id="element-2922">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2" data-id="element-2923">
              <StoreIcon className="w-5 h-5 text-mavis-teal" data-id="element-2924" />
              Vendors
            </h2>
            <span className="px-2 py-0.5 bg-mavis-teal/10 text-mavis-teal text-xs font-bold rounded-none" data-id="element-2925">
              {filteredVendors.length}
            </span>
          </div>
          <div className="relative" data-id="element-2926">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2927" />
            <input type="text" placeholder="Search vendors..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-none text-sm focus:outline-none focus:ring-1 focus:ring-mavis-teal focus:border-mavis-teal transition-colors" data-id="element-2928" />

          </div>
        </div>

        {/* Vendor List */}
        <div className="flex-1 overflow-y-auto" data-id="element-2929">
          {paginatedVendors.length === 0 ? <div className="p-6 text-center text-gray-500 text-sm" data-id="element-2930">
              No vendors found matching your search.
            </div> : <div className="divide-y divide-gray-100" data-id="element-2931">
              {paginatedVendors.map(vendor => {
            const isSelected = selectedVendorId === vendor.id;
            const color = getStatusColor(vendor.status);
            return <div key={vendor.id} onClick={() => setSelectedVendorId(vendor.id)} className={`p-3 cursor-pointer transition-colors flex justify-between items-center ${isSelected ? 'bg-mavis-teal/5 border-l-2 border-l-mavis-teal' : 'hover:bg-gray-50 border-l-2 border-l-transparent'}`} data-id="element-2932">

                    <div className="flex items-center gap-3 overflow-hidden" data-id="element-2933">
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{
                  backgroundColor: color
                }} data-id="element-2934" />

                      <div className="truncate" data-id="element-2935">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-mavis-teal font-bold' : 'text-gray-900'}`} data-id="element-2936">

                          {vendor.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate" data-id="element-2937">
                          {vendor.location}
                        </p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-2" data-id="element-2938">
                      <p className="text-sm font-mono font-bold text-gray-900" data-id="element-2939">
                        {formatZAR(vendor.salesToday)}
                      </p>
                      <p className="text-xs text-gray-500 flex items-center justify-end gap-0.5" data-id="element-2940">
                        {vendor.rating.toFixed(1)}{' '}
                        <StarIcon className="w-3 h-3 text-mavis-gold fill-mavis-gold" data-id="element-2941" />
                      </p>
                    </div>
                  </div>;
          })}
            </div>}
        </div>

        {/* Pagination Footer */}
        <div className="p-3 border-t border-gray-200 bg-gray-50/80 flex items-center justify-between" data-id="element-2942">
          <span className="text-xs text-gray-500 font-medium" data-id="element-2943">
            {filteredVendors.length > 0 ? <>
                {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredVendors.length)}{' '}
                of {filteredVendors.length}
              </> : '0 vendors'}
          </span>
          <div className="flex gap-1" data-id="element-2944">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1 || filteredVendors.length === 0} className="p-1.5 bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors" data-id="element-2945">

              <ChevronLeftIcon className="w-4 h-4" data-id="element-2946" />
            </button>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || filteredVendors.length === 0} className="p-1.5 bg-white border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-none transition-colors" data-id="element-2947">

              <ChevronRightIcon className="w-4 h-4" data-id="element-2948" />
            </button>
          </div>
        </div>
      </div>
    </div>;
}